import { TileProps } from "@app/shared/interfaces/dashboard";
import { useContext } from "react";
import * as React from "react";
import { Link } from "react-router-dom";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons"
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";

export const AdminDashboard: React.FC<React.PropsWithChildren<unknown>> = () => {

  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;

  const [tiles] = React.useState<TileProps[]>([
    {
      allowedRoles: [Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN],
      icon: QesIcons.BuildingV7,
      route: "client-administration",
      title: "Client Administration",
    },
    {
      allowedRoles: [Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN],
      icon: QesIcons.UsersV1,
      route: "user-administration",
      title: "User Administration",
    },
    {
      allowedRoles: [Roles.QUEST_ADMIN],
      icon: QesIcons.GlassesV2,
      route: "user-sessions",
      title: "User Sessions",
    },
    {
      allowedRoles: [Roles.SOFTWARE_LICENSE_ADMIN],
      icon: QesIcons.FileV4,
      route: "client-license/manager",
      title: "QAS Software Licenses"
    },
    {
      allowedRoles: [Roles.QUEST_ADMIN, Roles.SOFTWARE_LICENSE_ADMIN],
      icon: QesIcons.RocketV2,
      route: "software-releases",
      title: "QAS Software Releases"
    },
    {
      allowedRoles: [Roles.CLIENT_ADMIN],
      icon: QesIcons.UserV1,
      route: "provider-results",
      title: "Provider Results"
    },
    {
      allowedRoles: [Roles.QUEST_ADMIN, Roles.CLIENT_ADMIN],
      icon: QesIcons.LogInV1,
      route: "login-statistics",
      title: "User Login Stats"
    },
    {
      allowedRoles: [Roles.QUEST_DOCUMENT_ADMIN],
      icon: QesIcons.PencilV1,
      route: "manage-tutorials",
      title: "Manage Tutorials"
    },
    {
      allowedRoles: [Roles.CLIENT_ADMIN, Roles.QUEST_ADMIN],
      icon: QesIcons.LockV1,
      route: "role-groups",
      title: "Role Groups",
    },
    {
      allowedRoles: [Roles.INTERNAL_ONLY_CMS_EXCEPTION_DOCUMENT_MANAGER],
      icon: QesIcons.FlagV1,
      route: "specialty-group-exception",
      title: "Specialty Group Exception Template",
    },
    {
      allowedRoles: [Roles.CLIENT_ADMIN, Roles.QUEST_ADMIN, Roles.RESOURCE_ADMIN],
      icon: QesIcons.Folder,
      route: "manage-templates",
      title: "Manage Templates",
    },
    {
      allowedRoles: [Roles.NJDOBI_SELF_SERVICE_FUNCTIONS],
      icon: QesIcons.List,
      route: "view-manifest",
      title: "View Manifest",
    }
  ]);

  return (
    <Page documentTitle="Admin Dashboard">
      <Breadcrumbs template={BreadcrumbTemplateEnum.Admin} items={["Dashboard"]} />
      <PageInner>
        <PageHeader title="Admin Dashboard" disableHR={false} />
        <PageSection>
          <div className="container mt-3">
            {tiles.map((tile) =>             
              (!tile.requiredFeatureFlag &&
                <Tile key={tile.title} icon={tile.icon} route={tile.route} title={tile.title} allowedRoles={tile.allowedRoles} isV1={tile.isV1} />)
              || (!!tile.requiredFeatureFlag && featureFlags.has(tile.requiredFeatureFlag) && 
                <Tile key={tile.title} icon={tile.icon} route={tile.route} title={tile.title} allowedRoles={tile.allowedRoles} isV1={tile.isV1} />)
            )}
          </div>
        </PageSection>
      </PageInner>
    </Page>
  );
};

const Tile: React.FC<React.PropsWithChildren<TileProps>> = ({ icon, route = "#", title, allowedRoles, isV1 }) => {
  if (isV1) {
    return (
      <RoleRestricted allowedRoles={allowedRoles}>
        <a href={`${window.uiEndpoint}/${route}`} className="dashboard-tile col-4 mb-3">
          {icon ? <QesIcon iconName={icon} isDecorative={true} /> : null}
          <h6>{title}</h6>
        </a>
      </RoleRestricted >
    );
  } else {
    return (
      <RoleRestricted allowedRoles={allowedRoles}>
        <Link to={`/admin/${route}`} className="dashboard-tile col-4">
          {icon ? <QesIcon iconName={icon} isDecorative={true} /> : null}
          <h6>{title}</h6>
        </Link>
      </RoleRestricted>
    );
  }
};
